import React from 'react';
import { View, StyleSheet } from 'react-native';
import {
  Text,
  TextInput,
  Card,
  Button,
  IconButton,
  useTheme,
} from 'react-native-paper';
import { useFieldArray, useFormContext, Controller } from 'react-hook-form';

export function RecipeIngredients() {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ingredients',
  });

  const addIngredient = () => {
    append({
      name: '',
      amount: 0,
      unit: '',
      notes: '',
    });
  };

  const removeIngredient = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <View style={styles.container}>
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.header}>
            <Text variant='headlineSmall' style={styles.sectionTitle}>
              Ingredients
            </Text>
            <Button
              mode='contained'
              onPress={addIngredient}
              icon='plus'
              style={styles.addButton}
              contentStyle={styles.addButtonContent}
              labelStyle={styles.addButtonLabel}
            >
              Add
            </Button>
          </View>

          {fields.map((field, index) => (
            <Card key={field.id} style={styles.ingredientCard}>
              <Card.Content>
                <View style={styles.ingredientHeader}>
                  <Text variant='titleMedium'>Ingredient {index + 1}</Text>
                  <IconButton
                    icon='delete'
                    size={20}
                    onPress={() => removeIngredient(index)}
                    disabled={fields.length === 1}
                    iconColor={
                      fields.length === 1
                        ? theme.colors.outline
                        : theme.colors.error
                    }
                  />
                </View>

                {/* Ingredient Name */}
                <Controller
                  control={control}
                  name={`ingredients.${index}.name`}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextInput
                      label='Ingredient Name *'
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={!!errors.ingredients?.[index]?.name}
                      style={styles.input}
                      placeholder='e.g., Coffee beans, Water, Milk'
                    />
                  )}
                />
                {errors.ingredients?.[index]?.name && (
                  <Text
                    variant='bodySmall'
                    style={[styles.errorText, { color: theme.colors.error }]}
                  >
                    {errors.ingredients[index].name.message as string}
                  </Text>
                )}

                {/* Amount and Unit */}
                <View style={styles.row}>
                  <View style={styles.amountField}>
                    <Controller
                      control={control}
                      name={`ingredients.${index}.amount`}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextInput
                          label='Amount *'
                          value={value?.toString() || ''}
                          onChangeText={text => onChange(parseFloat(text) || 0)}
                          onBlur={onBlur}
                          keyboardType='numeric'
                          error={!!errors.ingredients?.[index]?.amount}
                          style={styles.input}
                          placeholder='20'
                        />
                      )}
                    />
                  </View>
                  <View style={styles.unitField}>
                    <Controller
                      control={control}
                      name={`ingredients.${index}.unit`}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextInput
                          label='Unit *'
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          error={!!errors.ingredients?.[index]?.unit}
                          style={styles.input}
                          placeholder='g, ml, cups'
                        />
                      )}
                    />
                  </View>
                </View>

                {/* Notes */}
                <Controller
                  control={control}
                  name={`ingredients.${index}.notes`}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextInput
                      label='Notes (optional)'
                      value={value || ''}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      multiline
                      numberOfLines={2}
                      style={styles.input}
                      placeholder='Special instructions or preferences...'
                    />
                  )}
                />
              </Card.Content>
            </Card>
          ))}

          {fields.length === 0 && (
            <View style={styles.emptyState}>
              <Text
                variant='bodyMedium'
                style={[
                  styles.emptyText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                No ingredients added yet
              </Text>
              <Button
                mode='outlined'
                onPress={addIngredient}
                icon='plus'
                style={[styles.emptyButton, styles.addButton]}
                contentStyle={styles.addButtonContent}
                labelStyle={styles.addButtonLabel}
              >
                Add Your First Ingredient
              </Button>
            </View>
          )}

          {errors.ingredients && (
            <Text
              variant='bodySmall'
              style={[styles.errorText, { color: theme.colors.error }]}
            >
              {errors.ingredients.message as string}
            </Text>
          )}
        </Card.Content>
      </Card>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Ingredients
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Be specific about coffee bean origin and roast{'\n'}• Use precise
            measurements for consistency{'\n'}• Include water quality notes if
            important{'\n'}• Mention any special preparation needed{'\n'}• List
            ingredients in order of use
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 24,
  },
  card: {
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  ingredientCard: {
    marginBottom: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  ingredientHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  input: {
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  amountField: {
    flex: 1,
  },
  unitField: {
    flex: 1,
  },
  errorText: {
    marginTop: 4,
    marginBottom: 8,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    marginBottom: 16,
  },
  emptyButton: {
    minWidth: 200,
  },
  addButton: {
    borderRadius: 20,
  },
  addButtonContent: {
    height: 40,
    paddingHorizontal: 16,
  },
  addButtonLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
